import { api } from './api';
import { logAnalyticsEvent } from './firebase';

type Props = Record<string, unknown>;

/** Money is held in paise; GA4 wants rupees. */
const rupees = (paise: unknown): number | undefined =>
  typeof paise === 'number' && Number.isFinite(paise) ? Math.round(paise) / 100 : undefined;

const text = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** Drop what we do not have, so GA4 never shows "undefined". */
const clean = (o: Props): Props => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

/**
 * Our event names and properties, in GA4's vocabulary. The recommended names
 * (view_item, add_to_cart, begin_checkout, purchase ...) are what light up the
 * built-in e-commerce reports. Anything unknown is sent as it is.
 */
export function toGa4(name: string, props: Props = {}): { name: string; params: Props } {
  switch (name) {
    case 'page_view':
      return {
        name,
        params: {
          page_path: text(props.path),
          page_location: typeof window === 'undefined' ? undefined : window.location.href,
          page_title: typeof document === 'undefined' ? undefined : document.title,
        },
      };
    case 'view_item':
      return {
        name,
        params: clean({
          currency: 'INR',
          value: rupees(props.price),
          items: [clean({ item_id: text(props.productId), item_name: text(props.name), price: rupees(props.price) })],
        }),
      };
    case 'add_to_cart':
      return {
        name,
        params: { currency: 'INR', items: [clean({ item_id: text(props.variantId), quantity: props.quantity })] },
      };
    case 'wishlist_add':
      return { name: 'add_to_wishlist', params: { currency: 'INR', items: [{ item_id: text(props.productId) }] } };
    case 'begin_checkout':
      return { name, params: clean({ currency: 'INR', value: rupees(props.value) }) };
    case 'purchase':
      return {
        name,
        params: clean({ transaction_id: text(props.orderId), currency: 'INR', value: rupees(props.value), payment_type: text(props.payment) }),
      };
    case 'coupon_apply':
      return { name, params: clean({ coupon: text(props.code) }) };
    case 'banner_click':
      return { name: 'select_promotion', params: clean({ creative_name: text(props.trackingKey), location_id: text(props.placement) }) };
    case 'custom_order_submit':
      return { name: 'generate_lead', params: clean({ lead_source: 'custom_order', product_type: text(props.productType) }) };
    default:
      return {
        name,
        params: Object.fromEntries(Object.entries(props).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v))),
      };
  }
}

/** Fire-and-forget store analytics events (spec section 18): our own log and Google Analytics. */
export function track(name: string, props: Props = {}): void {
  try {
    void api.post('/content/events', { name, path: window.location.pathname, props });
  } catch {
    /* never block the UI on analytics */
  }
  try {
    const ga = toGa4(name, props);
    void logAnalyticsEvent(ga.name, ga.params);
  } catch {
    /* nor on Google Analytics */
  }
}
